/**
 * Fund Create Dialog — Modal Form
 *
 * Fields: name*, fund_code, sfdr_category, status, base_currency, target_size,
 *         vintage_year, geography_focus (tags), sector_focus (tags)
 *
 * POST /api/v1/impact-core/funds/
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { apiClient } from '@/api/client';
import { useTranslation } from '@/i18n';
import type { FundCreatePayload, SFDRClassification, FundStatus } from '@/types';

interface FundCreateDialogProps {
  onClose: () => void;
  onCreated: () => void;
}

const CURRENCIES = ['USD', 'EUR', 'GBP', 'CHF', 'JPY', 'ZAR', 'KES', 'NGN'];

export function FundCreateDialog({ onClose, onCreated }: FundCreateDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [fundCode, setFundCode] = useState('');
  const [sfdrCategory, setSfdrCategory] = useState<SFDRClassification>('article_8');
  const [status, setStatus] = useState<FundStatus>('fundraising');
  const [currency, setCurrency] = useState('USD');
  const [targetSize, setTargetSize] = useState('');
  const [vintageYear, setVintageYear] = useState(new Date().getFullYear().toString());
  const [geoInput, setGeoInput] = useState('');
  const [geoTags, setGeoTags] = useState<string[]>([]);
  const [sectorInput, setSectorInput] = useState('');
  const [sectorTags, setSectorTags] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: async (payload: FundCreatePayload) => {
      const res = await apiClient.post('/api/v1/impact-core/funds/', payload);
      return res.data;
    },
    onSuccess: () => {
      onCreated();
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: string | Array<{ msg: string; loc: string[] }> } } };
      const detail = axiosErr.response?.data?.detail;
      if (typeof detail === 'string') {
        setErrors({ _general: detail });
      } else if (Array.isArray(detail)) {
        const mapped: Record<string, string> = {};
        for (const item of detail) {
          const field = item.loc[item.loc.length - 1] ?? '_general';
          mapped[field] = item.msg;
        }
        setErrors(mapped);
      } else {
        setErrors({ _general: t('error.createFailed') });
      }
    },
  });

  function handleAddTag(
    value: string,
    tags: string[],
    setTags: (t: string[]) => void,
    setInput: (v: string) => void,
  ) {
    const trimmed = value.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setInput('');
  }

  function handleRemoveTag(tag: string, tags: string[], setTags: (t: string[]) => void) {
    setTags(tags.filter((t) => t !== tag));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    if (!name.trim()) {
      setErrors({ name: 'Fund name is required' });
      return;
    }

    const payload: FundCreatePayload = {
      name: name.trim(),
      fund_code: fundCode.trim() || undefined,
      sfdr_category: sfdrCategory,
      status,
      base_currency: currency,
      target_size: targetSize ? Number(targetSize) : undefined,
      vintage_year: vintageYear ? Number(vintageYear) : undefined,
      geography_focus: geoTags.length > 0 ? geoTags : undefined,
      sector_focus: sectorTags.length > 0 ? sectorTags : undefined,
    };

    createMutation.mutate(payload);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-xl border border-ic-border bg-ic-card shadow-[0_20px_40px_rgba(0,0,0,0.16)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-ic-border px-6 py-4">
          <h2 className="text-sm font-semibold text-ic-text-primary">{t('action.createFund')}</h2>
          <button onClick={onClose} className="text-ic-text-tertiary hover:text-ic-text-primary">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
          {errors._general && (
            <div className="rounded-md bg-red-500/10 border border-red-500/20 px-3 py-2 text-xs text-red-400">
              {errors._general}
            </div>
          )}

          {/* Name */}
          <FieldGroup label={`${t('form.fundName')} *`} error={errors.name}>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Africa Climate Fund I"
              className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
            />
          </FieldGroup>

          {/* Code + SFDR row */}
          <div className="grid grid-cols-2 gap-4">
            <FieldGroup label={t('form.fundCode')} error={errors.fund_code}>
              <input
                type="text"
                value={fundCode}
                onChange={(e) => setFundCode(e.target.value)}
                placeholder="ACF-I"
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
              />
            </FieldGroup>
            <FieldGroup label={t('form.sfdrCategory')} error={errors.sfdr_category}>
              <select
                value={sfdrCategory}
                onChange={(e) => setSfdrCategory(e.target.value as SFDRClassification)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="article_6">Article 6</option>
                <option value="article_8">Article 8</option>
                <option value="article_9">Article 9</option>
              </select>
            </FieldGroup>
          </div>

          {/* Status + Currency */}
          <div className="grid grid-cols-2 gap-4">
            <FieldGroup label={t('table.status')} error={errors.status}>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as FundStatus)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                <option value="fundraising">{t('status.fundraising')}</option>
                <option value="investing">{t('status.investing')}</option>
                <option value="harvesting">{t('status.harvesting')}</option>
                <option value="closed">{t('status.closed')}</option>
              </select>
            </FieldGroup>
            <FieldGroup label={t('form.baseCurrency')} error={errors.base_currency}>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-secondary focus:border-ic-accent focus:outline-none"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </FieldGroup>
          </div>

          {/* Target Size + Vintage */}
          <div className="grid grid-cols-2 gap-4">
            <FieldGroup label={`${t('form.targetSize')} (USD)`} error={errors.target_size}>
              <input
                type="number"
                value={targetSize}
                onChange={(e) => setTargetSize(e.target.value)}
                placeholder="100000000"
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:border-ic-accent focus:outline-none"
              />
            </FieldGroup>
            <FieldGroup label={t('form.vintageYear')} error={errors.vintage_year}>
              <input
                type="number"
                value={vintageYear}
                onChange={(e) => setVintageYear(e.target.value)}
                min="2000"
                max="2100"
                className="w-full rounded-md border border-ic-border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:border-ic-accent focus:outline-none"
              />
            </FieldGroup>
          </div>

          {/* Geography Focus (tags) */}
          <FieldGroup label={t('form.geographyFocus')} error={errors.geography_focus}>
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-ic-border bg-ic-input px-2 py-1.5 min-h-[36px]">
              {geoTags.map((tag) => (
                <Tag key={tag} label={tag} onRemove={() => handleRemoveTag(tag, geoTags, setGeoTags)} />
              ))}
              <input
                type="text"
                value={geoInput}
                onChange={(e) => setGeoInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    handleAddTag(geoInput, geoTags, setGeoTags, setGeoInput);
                  }
                }}
                placeholder={geoTags.length === 0 ? 'Type and press Enter...' : ''}
                className="flex-1 min-w-[80px] bg-transparent text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:outline-none"
              />
            </div>
          </FieldGroup>

          {/* Sector Focus (tags) */}
          <FieldGroup label={t('form.sectorFocus')} error={errors.sector_focus}>
            <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-ic-border bg-ic-input px-2 py-1.5 min-h-[36px]">
              {sectorTags.map((tag) => (
                <Tag key={tag} label={tag} onRemove={() => handleRemoveTag(tag, sectorTags, setSectorTags)} />
              ))}
              <input
                type="text"
                value={sectorInput}
                onChange={(e) => setSectorInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    handleAddTag(sectorInput, sectorTags, setSectorTags, setSectorInput);
                  }
                }}
                placeholder={sectorTags.length === 0 ? 'Type and press Enter...' : ''}
                className="flex-1 min-w-[80px] bg-transparent text-xs text-ic-text-primary placeholder:text-ic-text-tertiary focus:outline-none"
              />
            </div>
          </FieldGroup>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-ic-border px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-ic-border px-4 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
          >
            {t('action.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            className="rounded-md bg-ic-accent px-4 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors"
          >
            {createMutation.isPending ? `${t('action.create')}...` : t('action.createFund')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────

function FieldGroup({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-ic-text-tertiary">
        {label}
      </label>
      {children}
      {error && <p className="mt-1 text-[10px] text-red-400">{error}</p>}
    </div>
  );
}

function Tag({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 rounded bg-ic-accent/15 px-2 py-0.5 text-[10px] font-medium text-ic-accent">
      {label}
      <button onClick={onRemove} className="hover:text-white">
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}
